import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import ExpenseService from '../../services/ExpenseService';
import { formatDate, formatMoney } from '../../utils/analyticsFormat';

// Offered even before any expense uses them.
const SUGGESTED_CATEGORIES = [
  'Voice over',
  'Editing',
  'Thumbnails',
  'Footage',
  'Software & AI tools',
  'Proxies',
  'Channel purchase',
  'Ads',
  'Other',
];
const MAX_AMOUNT = 1000000;
const MAX_CATEGORY_LENGTH = 40;
const MAX_DESCRIPTION_LENGTH = 200;

const categoryKey = (category) => category.trim().replace(/\s+/g, ' ').toLowerCase();

// The user's categories plus the suggestions, once each. An existing
// category's spelling wins over a suggestion's.
function categoryChoices(existingCategories) {
  const byKey = new Map();
  for (const category of [...existingCategories, ...SUGGESTED_CATEGORIES]) {
    if (!byKey.has(categoryKey(category))) byKey.set(categoryKey(category), category);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

// Today on the user's calendar, as YYYY-MM-DD.
function localToday() {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function validAmount(text) {
  const amount = Number(text);
  return text !== '' && Number.isFinite(amount) && amount >= 0.01 && amount <= MAX_AMOUNT;
}

function apiError(error, fallback) {
  return error.response?.data?.error || fallback;
}

/**
 * Adds an expense, or edits `expense` when given. A new expense starts on
 * today and on `defaultChannelId` (null for general).
 */
export function ExpenseDialog({ expense, defaultChannelId, channels, categories, onClose, onSaved }) {
  const [date, setDate] = useState(expense?.date || localToday());
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [category, setCategory] = useState(expense?.category || '');
  const [description, setDescription] = useState(expense?.description || '');
  const [channelId, setChannelId] = useState(expense ? expense.channelId || '' : defaultChannelId || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const choices = categoryChoices(categories);
  const valid = Boolean(date) && validAmount(amount) && category.trim() !== '';

  async function save() {
    setSaving(true);
    setError('');
    // A typed category reuses the spelling of a matching choice.
    const typed = category.trim().replace(/\s+/g, ' ');
    const fields = {
      date,
      amount: Number(amount),
      category: choices.find((choice) => categoryKey(choice) === categoryKey(typed)) || typed,
      description: description.trim(),
      channelId: channelId || null,
    };
    try {
      if (expense) await ExpenseService.update(expense.id, fields);
      else await ExpenseService.create(fields);
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Could not save the expense.'));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{expense ? 'Edit expense' : 'Add expense'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Amount"
            type="number"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            error={amount !== '' && !validAmount(amount)}
            helperText={amount !== '' && !validAmount(amount) ? 'Enter an amount from $0.01 to $1,000,000.' : null}
            inputProps={{ min: 0.01, max: MAX_AMOUNT, step: 0.01 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            autoFocus
            required
          />
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            error={!date}
            helperText="The day the money was spent."
            InputLabelProps={{ shrink: true }}
            required
          />
          <Autocomplete
            freeSolo
            options={choices}
            value={category}
            inputValue={category}
            onInputChange={(_, next) => setCategory(next)}
            onChange={(_, next) => setCategory(next || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                required
                helperText="Choose a category, or type a new one."
                inputProps={{ ...params.inputProps, maxLength: MAX_CATEGORY_LENGTH }}
              />
            )}
          />
          <TextField
            select
            label="Channel"
            value={channelId}
            onChange={(event) => setChannelId(event.target.value)}
            helperText="Expenses for a channel also count in its row and folder in Analytics."
            // General is the empty value, which a select hides by default.
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">General (not for one channel)</MenuItem>
            {channels.map((channel) => (
              <MenuItem key={channel.id} value={channel.id}>
                {channel.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Vendor, plan, or what it covered"
            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !valid}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {expense ? 'Save' : 'Add expense'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function DeleteExpenseDialog({ expense, onClose, onDeleted }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function remove() {
    setSaving(true);
    try {
      await ExpenseService.remove(expense.id);
      onDeleted();
    } catch (err) {
      setError(apiError(err, 'Could not delete the expense.'));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete this expense?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {formatMoney(expense.amount)} for {expense.category} on {formatDate(expense.date)}. It
          will no longer count against your revenue in Analytics.
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={remove} disabled={saving}>
          Delete expense
        </Button>
      </DialogActions>
    </Dialog>
  );
}
